/**
 * TutorConversationView — presentational: messages list, budget banner,
 * send-error banner, composer, and citation popup for ONE tutor
 * conversation. Consumes `useTutorConversation`'s result (spread as props);
 * owns no state/fetching of its own — same posture as TutorMessageBubble.tsx
 * / TutorCitationDialog.tsx.
 *
 * Extracted out of TutorOverlay.tsx's "main column" JSX unchanged (down to
 * the `max-w-5xl mx-auto` content constraint — harmless in TutorDrawer's
 * ~400px panel since `max-width` only caps, it never forces a wider box).
 * Reused by both TutorOverlay (next to its own conversations sidebar) and
 * TutorDrawer (no sidebar, single conversation).
 */

import * as React from 'react'
import Text from '@arianna/design-system/Text'
import type { UseTutorConversationResult } from './useTutorConversation.js'
import { TutorMessageBubble } from './TutorMessageBubble.js'
import { TutorCitationDialog } from './TutorCitationDialog.js'
import { formatResetsAt } from './tutorViewModel.js'

export interface TutorConversationViewProps extends UseTutorConversationResult {
  /** Shown when there are no messages yet. Differs between TutorOverlay
   *  (manual-wide prompt) and TutorDrawer (block-specific prompt) — see
   *  callers. */
  emptyStateMessage: string
  /** Whether to show the empty-state placeholder. Computed by the caller
   *  (not derived here) because TutorOverlay additionally waits on its own
   *  conversations-list load, a concern this view knows nothing about. */
  isEmpty: boolean
  /**
   * Compact layout for TutorDrawer's ~400px panel: tighter padding/gaps
   * throughout, threaded down to TutorMessageBubble for its own compact
   * rendering (label-above-bubble instead of a lateral avatar, wider
   * bubbles). Default false — TutorOverlay's full-screen layout, unchanged.
   */
  compact?: boolean
}

export function TutorConversationView({
  emptyStateMessage,
  isEmpty,
  compact = false,
  messages,
  messagesLoading,
  draft,
  setDraft,
  sending,
  sendError,
  isBlocked,
  limitResetsAt,
  handleSend,
  handleComposerKeyDown,
  handleFeedback,
  handleRunAction,
  citationPopup,
  handleOpenCitation,
  handleCloseCitationPopup,
  handleOpenCitationInManual,
  scrollEndRef,
}: TutorConversationViewProps) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className={`flex-1 overflow-y-auto ${compact ? 'px-3 py-4' : 'px-6 py-5'}`}>
        <div className={`max-w-5xl mx-auto flex flex-col ${compact ? 'gap-3' : 'gap-4'}`}>
          {isEmpty && (
            <div className="flex-1 flex items-center justify-center">
              <Text variant="lead" className="text-ink-3 text-center max-w-sm">
                {emptyStateMessage}
              </Text>
            </div>
          )}
          {messagesLoading && (
            <div className="flex items-center justify-center py-8">
              <Text variant="meta">Caricamento conversazione…</Text>
            </div>
          )}
          {!messagesLoading &&
            messages.map((message) => (
              <TutorMessageBubble
                key={message.id}
                message={message}
                onOpenCitation={handleOpenCitation}
                onRunAction={handleRunAction}
                onFeedback={(value) => handleFeedback(message.id, message.feedback, value)}
                compact={compact}
              />
            ))}
          <div ref={scrollEndRef} />
        </div>
      </div>

      {isBlocked && limitResetsAt != null && (
        <div className={`max-w-5xl mx-auto w-full mb-3 ${compact ? 'px-3' : 'px-5'}`}>
          <div className="flex items-center gap-3 rounded-md border border-gold bg-accent-bg px-4 py-3">
            <span aria-hidden="true" className="text-gold text-lg">
              ⏱
            </span>
            <div className="flex-1">
              <Text variant="ui" className="text-ink">
                Limite di domande raggiunto
              </Text>
              <Text variant="meta">Riprova alle {formatResetsAt(limitResetsAt)}.</Text>
            </div>
          </div>
        </div>
      )}

      {sendError != null && (
        <div className={`max-w-5xl mx-auto w-full mb-3 ${compact ? 'px-3' : 'px-5'}`}>
          <Text variant="meta" className="text-red">
            {sendError}
          </Text>
        </div>
      )}

      <div className={`border-t border-line shrink-0 ${compact ? 'px-3 py-3' : 'px-5 py-4'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleComposerKeyDown}
              disabled={isBlocked}
              placeholder="Chiedi al tutor…"
              rows={1}
              className="flex-1 resize-none font-sans text-sm text-ink bg-surface placeholder:text-ink-4 focus:outline-none border border-line rounded-md px-3 py-2 focus:border-ink-2 transition-colors max-h-32 disabled:opacity-60"
            />
            <button
              onClick={() => void handleSend()}
              disabled={sending || isBlocked || draft.trim().length === 0}
              title="Invia"
              className="inline-flex items-center justify-center size-9 rounded-md bg-accent text-white border border-accent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 22 22"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="11" y1="18" x2="11" y2="4" />
                <polyline points="5 10 11 4 17 10" />
              </svg>
            </button>
          </div>
          <Text variant="meta" className="mt-2 block">
            {compact
              ? 'Le citazioni rimandano al manuale.'
              : 'Le citazioni rimandano alla sezione del manuale, così puoi verificare.'}
          </Text>
        </div>
      </div>

      {citationPopup != null && (
        <TutorCitationDialog
          citation={citationPopup.citation}
          status={citationPopup.status}
          block={citationPopup.block}
          errorMessage={citationPopup.errorMessage}
          onClose={handleCloseCitationPopup}
          onOpenInManual={handleOpenCitationInManual}
        />
      )}
    </div>
  )
}
